import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { ChevronLeft, ChevronRight, X, Check, XCircle } from 'lucide-react';
import { adminApi } from '../api/admin';
import AdminLayout from '../components/AdminLayout';

export default function AdminResellerApplications() {
  usePageTitle("Administration — Demandes revendeurs — Lodiss");

  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApp, setSelectedApp] = useState<any | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [resendingActivation, setResendingActivation] = useState(false);
  const [activationResult, setActivationResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    loadApplications();
  }, [page, statusFilter]);

  const loadApplications = () => {
    setLoading(true);
    adminApi.getResellerApplications({ 
      page, 
      limit: 20, 
      status: statusFilter || undefined,
    })
      .then(res => {
        setApplications(res.items);
        setTotal(res.total);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleStatusChange = async (status: string) => {
    if (!selectedApp) return;
    setSaving(true);
    try {
      await adminApi.updateResellerApplication(selectedApp.id, { 
        status, 
        admin_notes: adminNotes || undefined 
      });
      setSelectedApp(null);
      setAdminNotes('');
      loadApplications();
    } catch (err) {
      console.error('Failed to update application:', err);
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (app: any) => {
    setSelectedApp(app);
    setAdminNotes(app.admin_notes || '');
    setActivationResult(null);
  };

  const handleResendActivation = async () => {
    if (!selectedApp) return;
    
    if (!window.confirm(`Renvoyer le lien d'activation à ${selectedApp.email} ?`)) {
      return;
    }
    
    setResendingActivation(true);
    setActivationResult(null);
    try {
      const res = await adminApi.resendActivation(selectedApp.id);
      if (res.data.success) {
        setActivationResult({
          success: true,
          message: `Lien renvoyé à ${res.data.sent_to}. Valable jusqu'au ${new Date(res.data.expires_at!).toLocaleString('fr-FR')}.`
        });
        loadApplications(); // Refresh list to update `last_activation_sent_at`
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || "Erreur lors de l'envoi de l'email.";
      setActivationResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setResendingActivation(false);
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <AdminLayout>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Demandes de revendeurs</h1>

        {/* Filter */}
        <div className="flex gap-4 mb-6">
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvé</option>
            <option value="rejected">Rejeté</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Entreprise</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Ville</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Compte</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center">
                    <div className="w-6 h-6 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : applications.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Aucune demande trouvée
                  </td>
                </tr>
              ) : (
                applications.map(app => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{app.company}</td>
                    <td className="px-4 py-3 text-sm">{app.email}</td>
                    <td className="px-4 py-3 text-sm">{app.city || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        app.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        app.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {app.status === 'pending' ? 'En attente' :
                         app.status === 'approved' ? 'Approuvé' : 'Rejeté'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {app.status === 'pending' ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                          Pas encore créé
                        </span>
                      ) : app.status === 'approved' && app.user_id ? (
                        <>
                          {app.user_status === 'inactive' ? (
                            <div>
                              <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700">En attente d'activation</span>
                              {app.last_activation_sent_at && (
                                <p className="text-[10px] text-gray-400 mt-1">
                                  Lien envoyé le {new Date(app.last_activation_sent_at).toLocaleDateString('fr-FR')}
                                </p>
                              )}
                            </div>
                          ) : (
                            <div>
                              <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">Compte actif</span>
                              {app.user_created_at && (
                                <p className="text-[10px] text-gray-400 mt-1">
                                  Depuis le {new Date(app.user_created_at).toLocaleDateString('fr-FR')}
                                </p>
                              )}
                            </div>
                          )}
                        </>
                      ) : app.status === 'approved' ? (
                         <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">Erreur de création</span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(app.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openDetail(app)}
                        className="text-sm text-lodiss-red hover:underline"
                      >
                        Voir
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500">
              {(page - 1) * 20 + 1} - {Math.min(page * 20, total)} sur {total}
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 border rounded-lg disabled:opacity-50">
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 border rounded-lg disabled:opacity-50">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {selectedApp && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold">Demande de {selectedApp.company}</h2>
                <button onClick={() => setSelectedApp(null)} className="p-1 hover:bg-gray-100 rounded">
                  <X size={20} />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Entreprise</p>
                    <p className="font-medium">{selectedApp.company}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium">{selectedApp.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Téléphone</p>
                    <p className="font-medium">{selectedApp.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Ville</p>
                    <p className="font-medium">{selectedApp.city || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">N° TVA (19%)</p>
                    <p className="font-medium">{selectedApp.tax_id || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Volume mensuel</p>
                    <p className="font-medium">{selectedApp.monthly_volume || '-'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Catégories</p>
                  <div className="flex gap-2 flex-wrap">
                    {(selectedApp.categories || []).map((cat: string, i: number) => (
                      <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">{cat}</span>
                    ))}
                  </div>
                </div>
                {selectedApp.message && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Message</p>
                    <p className="text-sm bg-gray-50 p-3 rounded">{selectedApp.message}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500 mb-1">Notes admin</p>
                  <textarea
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    rows={3}
                    placeholder="Notes internes..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-between p-4 border-t items-center">
                <div className="flex gap-2">
                  {selectedApp.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleStatusChange('approved')}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
                      >
                        <Check size={16} />
                        Approuver
                      </button>
                      <button
                        onClick={() => handleStatusChange('rejected')}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                      >
                        <XCircle size={16} />
                        Rejeter
                      </button>
                    </>
                  )}
                  {selectedApp.status === 'approved' && selectedApp.user_status === 'inactive' && (
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={handleResendActivation}
                        disabled={resendingActivation}
                        className="text-sm px-4 py-2 bg-lodiss-dark text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50 flex items-center justify-center min-w-[200px]"
                      >
                        {resendingActivation ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          "Renvoyer le lien d'activation"
                        )}
                      </button>
                      {activationResult && (
                        <span className={`text-xs ${activationResult.success ? 'text-green-600' : 'text-red-600'}`}>
                          {activationResult.message}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setSelectedApp(null)}
                  className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
